import type { Breadcrumb, ErrorEvent } from '@sentry/react';
import { redactText, redactDeep } from './pii-redaction.ts';

/**
 * What may leave the browser. The DOM breadcrumb is the risk: the element clicked
 * renders a client's name, so the path is kept and the text inside it is not.
 */

function stripAttributeValues(selector: string): string {
  return selector.replace(/\[([a-zA-Z-]+)=(?:"[^"]*"|'[^']*'|[^\]]*)\]/g, '[$1]');
}

function stripQuery(url: string): string {
  return url.split('?')[0]!;
}

export function scrubBrowserBreadcrumb(crumb: Breadcrumb): Breadcrumb {
  const isUi = typeof crumb.category === 'string' && crumb.category.startsWith('ui.');

  if (typeof crumb.message === 'string') {
    crumb.message = redactText(isUi ? stripAttributeValues(crumb.message) : crumb.message);
  }

  if (crumb.data !== undefined) {
    for (const [key, value] of Object.entries(crumb.data)) {
      if (typeof value !== 'string') continue;
      const stripped = key === 'url' || key === 'from' || key === 'to' ? stripQuery(value) : value;
      crumb.data[key] = redactText(isUi ? stripAttributeValues(stripped) : stripped);
    }
  }
  return crumb;
}

export function scrubBrowserEvent(event: ErrorEvent): ErrorEvent {
  if (typeof event.message === 'string') event.message = redactText(event.message);

  for (const value of event.exception?.values ?? []) {
    if (typeof value.value === 'string') value.value = redactText(value.value);
  }

  if (event.request !== undefined) {
    delete event.request.data;
    delete event.request.cookies;
    delete event.request.headers;
    if (typeof event.request.url === 'string') event.request.url = stripQuery(event.request.url);
    if (typeof event.request.query_string === 'string') event.request.query_string = '<removed>';
  }

  // Two DEFAULT integrations write `extra`: browserApiErrors (handler arguments) and a
  // non-Error throw (serialised whole). Nothing here sets it, so it goes.
  delete event.extra;
  delete event.user;
  delete event.tags;
  if (event.contexts !== undefined) {
    event.contexts = redactDeep(event.contexts) as typeof event.contexts;
  }
  return event;
}
